import { CALL_API } from 'redux-api-middleware';
import {
  get,
  keyBy,
  mapValues,
  range,
  every,
  omit,
  union,
  without,
  capitalize,
  isNil,
} from 'lodash';
import { listMediaFiles } from './constants';
import { createSelector } from 'reselect';
import { guid } from 'helpers/misc';
import { commonHeaders } from 'helpers/api';
import { reduceReducers, createReducer, fetchingStatus } from 'helpers/redux';
import paginate, { makeSelectors } from 'helpers/redux/paginate';
import { getConfig } from 'modules/config';
import { withConfirmation } from 'modules/confirmation';

export const namespace = 'engine';

const LISTING_INITIAL_ENGINES = 'vtn/engine/LISTING_INITIAL_ENGINES';
const DONE_LISTING_INITIAL_ENGINES = 'vtn/engine/DONE_LISTING_INITIAL_ENGINES';

const CREATE = 'vtn/engine/CREATE';
export const CREATE_SUCCESS = 'vtn/engine/CREATE_SUCCESS';
const CREATE_FAILURE = 'vtn/engine/CREATE_FAILURE';

const UPDATE = 'vtn/engine/UPDATE';
export const UPDATE_SUCCESS = 'vtn/engine/UPDATE_SUCCESS';
const UPDATE_FAILURE = 'vtn/engine/UPDATE_FAILURE';

const DELETE = 'vtn/engine/DELETE';
export const DELETE_SUCCESS = 'vtn/engine/DELETE_SUCCESS';
export const DELETE_FAILURE = 'vtn/engine/DELETE_FAILURE';

export const FETCH_ENGINE = 'vtn/engine/FETCH_ENGINE';
export const FETCH_ENGINE_SUCCESS = 'vtn/engine/FETCH_ENGINE_SUCCESS';
export const FETCH_ENGINE_FAILURE = 'vtn/engine/FETCH_ENGINE_FAILURE';

export const LIST = 'vtn/engine/LIST';
export const LIST_FILTERED = 'vtn/engines/LIST_FILTERED';
export const LIST_SUCCESS = 'vtn/engine/LIST_SUCCESS';
export const LIST_FAILURE = 'vtn/engine/LIST_FAILURE';

export const LIST_ENGINES_BUILDS = 'vtn/engine/LIST_ENGINES_BUILDS';
export const LIST_ENGINES_BUILDS_SUCCESS =
  'vtn/engine/LIST_ENGINES_BUILDS_SUCCESS';
export const LIST_ENGINES_BUILDS_FAILURE =
  'vtn/engine/LIST_ENGINES_BUILDS_FAILURE';

export const ENGINE_ACTION = 'vtn/engine/ENGINE_ACTION';
export const ENGINE_ACTION_SUCCESS = 'vtn/engine/ENGINE_ACTION_SUCCESS';
export const ENGINE_ACTION_FAILURE = 'vtn/engine/ENGINE_ACTION_FAILURE';

const FETCH_ENGINE_CATEGORIES = 'vtn/engine-wizard/FETCH_ENGINE_CATEGORIES';
export const FETCH_ENGINE_CATEGORIES_SUCCESS =
  'vtn/engine-wizard/FETCH_ENGINE_CATEGORIES_SUCCESS';
export const FETCH_ENGINE_CATEGORIES_FAILURE =
  'vtn/engine-wizard/FETCH_ENGINE_CATEGORIES_FAILURE';

export const BUILD_NODERED_ENGINE = 'vtn/engine/BUILD_NODERED_ENGINE';
export const BUILD_NODERED_ENGINE_SUCCESS =
  'vtn/engine/BUILD_NODERED_ENGINE_SUCCESS';
export const BUILD_NODERED_ENGINE_FAILURE =
  'vtn/engine/BUILD_NODERED_ENGINE_FAILURE';

const defaultState = {
  enginesById: {},
  recentBuildsById: {},
  engineOverview: {},
  failedFetchingEngines: [],
  isFetchingInitialEngines: false,
  isFetchingCategories: false,
  fetchingCategoriesFailed: false,
  isFetchingEngines: false,
  engineCategories: [],
  limit: 9999,
  offset: 0,
  filter: {},
};

const reducer = reduceReducers(
  paginate({
    types: [LIST, LIST_SUCCESS, LIST_FAILURE],
    invalidateOnTypes: [CREATE_SUCCESS, DELETE_SUCCESS],
    idAttribute: 'id',
    keyBy: (state, meta) => get(meta, 'resource', 'engine'),
  }),
  createReducer(defaultState, {
    // [CREATE](state, action) {
    //   return state;
    // },
    //
    [LISTING_INITIAL_ENGINES](state, action) {
      return {
        ...state,
        isFetchingInitialEngines: true,
      };
    },

    [DONE_LISTING_INITIAL_ENGINES](state, action) {
      return {
        ...state,
        isFetchingInitialEngines: false,
      };
    },

    [LIST](state, action) {
      return {
        ...state,
        isFetchingEngines: true,
      };
    },
    [LIST_FILTERED](state, action) {
      return {
        ...state,
        isFetchingEngines: true,
        limit: action.payload.limit,
        offset: action.payload.offset,
        filter: action.payload.filter,
      };
    },
    [LIST_SUCCESS](state, action) {
      return {
        ...state,
        isFetchingEngines: false,
        enginesById: {
          ...state.enginesById,
          ...keyBy(get(action.payload, 'data.results.records'), 'id'),
        },
      };
    },
    [LIST_FAILURE](state, action) {
      return {
        ...state,
        isFetchingEngines: false,
      };
    },

    [LIST_ENGINES_BUILDS](state, action) {
      return {
        ...state,
        isFetchingEngines: true,
      };
    },

    [LIST_ENGINES_BUILDS_SUCCESS](state, action) {
      return {
        ...state,
        isFetchingEngines: false,
        engineOverview: get(action.payload, 'data.engineOverview', {}),
        recentBuildsById: {
          ...state.recentBuildsById,
          ...keyBy(get(action.payload, 'data.recentBuilds.records'), 'id'),
        },
      };
    },

    [LIST_ENGINES_BUILDS_FAILURE](state, action) {
      return {
        ...state,
        isFetchingEngines: false,
      };
    },

    [FETCH_ENGINE_CATEGORIES](state, action) {
      const requestSuccessState = {
        ...state,
        isFetchingCategories: true,
        fetchingCategoriesFailed: false,
      };

      return action.error
        ? // handle requestError ie. offline
          this[FETCH_ENGINE_CATEGORIES_FAILURE](state, action)
        : requestSuccessState;
    },

    [FETCH_ENGINE_CATEGORIES_SUCCESS](state, action) {
      if (action.payload.errors && action.payload.errors.length) {
        return this[FETCH_ENGINE_CATEGORIES_FAILURE](state, action);
      }

      return {
        ...state,
        isFetchingCategories: false,
        fetchingCategoriesFailed: false,
        engineCategories: get(action.payload, 'data.engineCategories.records'),
      };
    },

    [FETCH_ENGINE_CATEGORIES_FAILURE](state, action) {
      return {
        ...state,
        isFetchingCategories: false,
        fetchingCategoriesFailed: true,
        engineCategories: [],
      };
    },

    [FETCH_ENGINE](state, action) {
      const requestSuccessState = {
        ...state,
        // todo: loading
        // isFetchingCategories: true,
        // fetchingCategoriesFailed: false
      };

      return action.error
        ? // handle requestError ie. offline
          this[FETCH_ENGINE_FAILURE](state, action)
        : requestSuccessState;
    },

    [FETCH_ENGINE_SUCCESS](state, action) {
      if (action.payload.errors && action.payload.errors.length) {
        return this[FETCH_ENGINE_FAILURE](state, action);
      }
      return {
        ...state,
        // isFetchingCategories: false,
        // fetchingCategoriesFailed: false,
        enginesById: {
          ...state.enginesById,
          [action.meta.id]: get(action.payload, 'data.result'),
        },
        failedFetchingEngines: without(
          state.failedFetchingEngines,
          action.meta.id
        ),
      };
    },

    [FETCH_ENGINE_FAILURE](state, action) {
      return {
        ...state,
        failedFetchingEngines: union(state.failedFetchingEngines, [
          action.meta.id,
        ]),
      };
    },

    [UPDATE_SUCCESS](state, action) {
      const engine = get(action.payload, 'data.result')
        ? get(action.payload, 'data.result')
        : state.enginesById[action.meta.id];

      return {
        ...state,
        enginesById: {
          ...state.enginesById,
          [action.meta.id]: engine,
        },
      };
    },

    [DELETE_SUCCESS](state, action) {
      const engineId = action.meta;
      return {
        ...state,
        enginesById: {
          ...omit(state.enginesById, engineId),
        },
      };
    },

    [ENGINE_ACTION_SUCCESS](state, action) {
      const engine = get(action.payload, 'data.result');

      return {
        ...state,
        enginesById: {
          ...state.enginesById,
          [action.meta.id]: engine,
        },
      };
    },
  })
);

export default reducer;

function local(state) {
  return state[namespace];
}

export function listingInitialEngines() {
  // todo: move to engines overview plugin
  return {
    type: LISTING_INITIAL_ENGINES,
  };
}

export function doneListingInitialEngines() {
  return {
    type: DONE_LISTING_INITIAL_ENGINES,
  };
}

export function isFetchingInitialEngines(state) {
  return local(state).isFetchingInitialEngines;
}

export function isFetchingEngines(state) {
  return local(state).isFetchingEngines;
}

export function filterEngines({ limit = 9999, offset = 0, filter = {} }) {
  return {
    type: LIST_FILTERED,
    payload: {
      limit: limit,
      offset: offset,
      filter: filter,
    },
  };
}

export function createEngine(values) {
  const query = `
    ${engineFieldsFragment}
    mutation createEngine($input: CreateEngine!) {
      result: createEngine(input: $input) {
        ...engineFields
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [CREATE, CREATE_SUCCESS, CREATE_FAILURE],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input: values,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export function updateEngine(values) {
  const input = omit(values, [
    'createdDateTime',
    'modifiedDateTime',
    'category',
    'ownerOrganizationId',
    'validStateActions',
    'state',
    'builds',
    'signedIconPath',
    'signedLogoPath',
  ]);

  const query = `
    ${engineFieldsFragment}
    mutation updateEngine($input: UpdateEngine!) {
      result: updateEngine(input: $input) {
        ...engineFields
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [UPDATE, UPDATE_SUCCESS, UPDATE_FAILURE].map((a) => ({
        type: a,
        meta: { id: values.id },
      })),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export function deleteEngine({ id }) {
  const query = `
    mutation deleteEngine($id: ID!) {
      result: deleteEngine(id: $id) {
        id
        message
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [DELETE, DELETE_SUCCESS, DELETE_FAILURE].map((a) => ({
        type: a,
        meta: { id },
      })),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          id,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export const confirmDeleteEngine = withConfirmation(
  deleteEngine,
  ({ name }) => ({
    message: `Are you sure you want to delete the engine "${name}"?
        You won't be able to undo this action`,
    confirmButtonLabel: 'Delete',
    cancelButtonLabel: 'Cancel',
  })
);

export function listEngines(
  { offset = 0, limit = 9999, filter = {}, resource, orderBy = [] } = {},
  { force } = {}
) {
  const requestId = guid();

  const query = `
    ${engineFieldsFragmentList}
    query($limit: Int, $offset: Int, $filter: EngineFilter, $orderBy: [EngineSortField]) {
      totalResults: engines(owned: true, offset: 0, limit: 9999, filter: $filter) {
        count
      }
      results: engines(limit: $limit, offset: $offset, owned: true, filter: $filter, orderBy: $orderBy) {
        records {
          ...engineFields
        }
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [LIST, LIST_SUCCESS, LIST_FAILURE].map((type) => ({
        type,
        meta: { requestId, offset, limit, resource, filter, orderBy },
      })),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      bailout: (state) => {
        if (force) {
          return false;
        }

        const engines = getPagedEngines(state, resource);
        const needStart = offset;
        const needEnd = offset + limit - 1;

        // bail if all needed engines exist
        return every(range(needStart, needEnd + 1), (index) => {
          return !!engines[index];
        });
      },
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          limit,
          offset,
          filter,
          orderBy,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export function listEnginesBuilds({
  offset = 0,
  limit = 5,
  resource,
  orderBy = [],
} = {}) {
  const requestId = guid();

  const query = `
  query listEnginesBuilds ($limit: Int, $offset: Int, $orderBy: [EngineBuildOrderBy!]) {
    recentBuilds: recentBuilds(offset: $offset, limit: $limit, orderBy: $orderBy) {
      records {
        id
        name
        version
        status
        engine {
          id
          name
          iconPath
          logoPath
          signedIconPath
          signedLogoPath
        }
      }
    }
    engineOverview: engineOverview {
      active
      disabled
      pending
      deleted
      draft
      ready
    }
  }
  `;

  return {
    [CALL_API]: {
      types: [
        LIST_ENGINES_BUILDS,
        LIST_ENGINES_BUILDS_SUCCESS,
        LIST_ENGINES_BUILDS_FAILURE,
      ].map((type) => ({
        type,
        meta: { requestId, offset, limit, resource, orderBy },
      })),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          limit,
          offset,
          orderBy,
        },
      }),
      headers: commonHeaders,
    },
  };
}

export function fetchEngineCategories({ type } = {}, { force } = {}) {
  const query = `
    query($type: String) {
      engineCategories(type: $type, limit: 999) {
        records {
          id
          name
          type {
            name
            description
          }
          iconClass
          libraryEntityIdentifierTypeIds
          description
          class {
            name
            description
            iconClass
          }
        }
      }
    }`;

  return {
    [CALL_API]: {
      types: [
        FETCH_ENGINE_CATEGORIES,
        FETCH_ENGINE_CATEGORIES_SUCCESS,
        FETCH_ENGINE_CATEGORIES_FAILURE,
      ],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          type,
        },
      }),
      headers: commonHeaders,
      bailout: (state) =>
        (local(state).isFetchingCategories ||
          local(state).engineCategories.length) &&
        !force,
    },
  };
}

export function fetchEngine(id) {
  const query = `
    ${engineFieldsFragment}
    query($id: ID!) {
      result: engine(id: $id) {
        ...engineFields
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [FETCH_ENGINE, FETCH_ENGINE_SUCCESS, FETCH_ENGINE_FAILURE].map(
        (type) => ({ type, meta: { id } })
      ),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { id },
      }),
      headers: commonHeaders,
      bailout: (state) => !!getEngines(state)[id],
    },
  };
}

export const validActions = ['disable', 'enable'];

export function doEngineAction(action, { id }) {
  const query = `
    ${engineFieldsFragment}
    mutation engineWorkflow($input: EngineWorkflow!) {
      result: engineWorkflow(input: $input) {
        ...engineFields
      }
    }
  `;
  return {
    [CALL_API]: {
      types: [ENGINE_ACTION, ENGINE_ACTION_SUCCESS, ENGINE_ACTION_FAILURE].map(
        (type) => ({
          type,
          meta: { id, action },
        })
      ),
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input: {
            id,
            action,
          },
        },
      }),
      headers: commonHeaders,
    },
  };
}

export const confirmDoEngineAction = withConfirmation(
  doEngineAction,
  (type, { name }) => ({
    message: `Are you sure you want to ${type} the engine "${name}"?`,
    confirmButtonLabel: capitalize(type),
    cancelButtonLabel: 'Cancel',
  })
);

export const getPagedEngines = createSelector(
  (state, resource) =>
    makeSelectors(resource).selectPaginationOrder(local(state)),
  (state, resource) => local(state).enginesById,
  (engineOrder, enginesById) => mapValues(engineOrder, (id) => enginesById[id])
);

export function getEngines(state) {
  return local(state).enginesById;
}

export function getRecentBuilds(state) {
  return local(state).recentBuildsById;
}

export function getEngineOverview(state) {
  return local(state).engineOverview;
}

export function getFilteredMediaFiles(engine) {
  const category = { ...engine.category };
  let categoryName = category.name;
  let className = category.class.name;

  categoryName = categoryName.toLowerCase();
  while (categoryName.includes(' ')) {
    categoryName = categoryName.replace(' ', '_');
  }
  let findingMediaFiles = listMediaFiles.filter(
    (classes) => classes.class === className
  );
  findingMediaFiles =
    findingMediaFiles.length > 0
      ? findingMediaFiles[0].category.filter(
          (categories) => categories.name === categoryName
        )
      : [];
  findingMediaFiles =
    findingMediaFiles.length > 0 ? findingMediaFiles[0].mediaFileUris : [];

  return findingMediaFiles;
}

export function handleTestingDetails(engine) {
  if (isNil(engine.testingDetails)) {
    engine.email = '';
    engine.mediaFileUri = '';
    engine.testingCustomFieldsKvp = [];
    return engine;
  }
  // eslint-disable-next-line no-param-reassign
  engine = { ...engine, ...engine.testingDetails };
  engine.email = engine.testingDetails.email;
  engine.mediaFileUri = engine.testingDetails.mediaFileUri;
  engine.customFields = [engine.customFields];
  engine.testingCustomFieldsKvp = [];
  let i = 0;
  while (i < engine.customFields.length) {
    for (let key in engine.customFields[i]) {
      engine.testingCustomFieldsKvp.push([]);
      engine.testingCustomFieldsKvp[
        engine.testingCustomFieldsKvp.length - 1
      ].push(key, engine.customFields[i][key]);
    }
    i++;
  }
  return engine;
}

export function fillOutEmptyFields(engine) {
  engine.description = engine.description
    ? engine.description
    : 'this is sample description';
  engine.useCases =
    isNil(engine.useCases) || engine.useCases.length !== 0
      ? engine.useCases
      : [];
  engine.libraryRequired = engine.libraryRequired
    ? engine.libraryRequired
    : false;
  engine.email = engine.email ? engine.email : null;
  engine.mediaFileUri = engine.mediaFileUri ? engine.mediaFileUri : null;
  if (engine.standaloneJobTemplates.length) {
    engine.standaloneJobTemplates.forEach((temp) => {
      if (temp.type === 'Upload') {
        engine.singleEngineUploadJobJson = temp.template.template;
      } else if (temp.type === 'Reprocess') {
        engine.singleEngineTdoJobJson = temp.template.template;
      }
    });
  }
  return engine;
}

export function getEngine(state, engineId) {
  return local(state).enginesById[engineId];
}

export function getListEnginesStatus(state, resource) {
  return makeSelectors(resource).selectFetchingStatus(local(state));
}

export function getLoadingEngines(state, resource) {
  return makeSelectors(resource).selectFetchingEntities(local(state));
}

export function getFailedLoadingEngines(state, resource) {
  return makeSelectors(resource).selectFailedFetchingEntities(local(state));
}

export function getTotalEngines(state, resource) {
  return makeSelectors(resource).selectTotalEntities(local(state));
}

export function getFailedFetchingEngines(state) {
  // failure for individually-fetched (by id) engines
  return local(state).failedFetchingEngines;
}

export function selectEngineCategories(state) {
  return local(state).engineCategories;
}

export function getFetchCategoriesStatus(state) {
  const fetching = local(state).isFetchingCategories;
  const failed = local(state).fetchingCategoriesFailed;

  if (fetching) {
    return fetchingStatus.fetching;
  }

  if (failed) {
    return fetchingStatus.failure;
  }

  if (selectEngineCategories(state).length) {
    return fetchingStatus.success;
  }

  return fetchingStatus.default;
}

export function selectEngineClasses(state) {
  const engineCategories = local(state).engineCategories;
  const engineClasses = engineCategories.reduce((acc, curr) => {
    if (!curr.class) {
      return acc;
    }
    if (!acc[curr.class.name]) {
      acc[curr.class.name] = {
        ...curr.class,
        capabilities: [curr],
      };
    } else {
      acc[curr.class.name].capabilities.push(curr);
    }
    return acc;
  }, {});
  const engineClassesSorted = {};
  Object.keys(engineClasses)
    .sort()
    .forEach((key) => {
      engineClassesSorted[key] = engineClasses[key];
    });
  return engineClassesSorted;
}

const engineFieldsFragment = `
fragment engineFields on Engine {
  id
  name
  state
  price
  categoryId
  category {
    name
    iconClass
    type {
      name
      description
    }
    class {
      name
      description
      iconClass
    }
    description
  }
  description
  deploymentModel
  signedIconPath
  signedLogoPath
  iconPath
  logoPath
  createdDateTime
  modifiedDateTime
  ownerOrganizationId
  validStateActions
  builds {
    count
    records{
      runtime
    }
  }
  fields {
    max
    min
    step
    type
    info
    name
    label
    options {
      key
      value
    }
    defaultValue
    required
  }
  libraryRequired
  useCases
  industries
  manifest
  testingDetails {
    email
    mediaFileUri
    customFields
    isCertified
  }
  standaloneJobTemplates {
    type
    template
  }
  edgeVersion
}
`;

const engineFieldsFragmentList = `
fragment engineFields on Engine {
  id
  name
  state
  price
  categoryId
  category {
    name
    iconClass
    type {
      name
      description
    }
    class {
      name
      description
      iconClass
    }
    description
  }
  description
  deploymentModel
  iconPath
  logoPath
  signedIconPath
  signedLogoPath
  createdDateTime
  modifiedDateTime
  ownerOrganizationId
  validStateActions
  builds {
    count
    records{
      runtime
    }
  }
  fields {
    max
    min
    step
    type
    info
    name
    label
    options {
      key
      value
    }
    defaultValue
    required
  }
  libraryRequired
  useCases
  industries
  manifest
  testingDetails {
    email
    mediaFileUri
    customFields
    isCertified
  }
  standaloneJobTemplates {
    type
    template
  }
  edgeVersion
}
`;

export const getEngineBuildQuery = () =>
  `query($id:ID!){
    engine(id: $id){
      builds(status: "deployed") {
        records{
          engine{
            name
            id
            state
            builds{
              records{
                id
                status
              }
            }
          }
        }
      }
    }
  }`;

export const createNewEngine = (values) => {
  const query = `mutation createEngine($input: CreateEngine!) {
      createEngine(input: $input) {
        id
        name
      }
    }
  `;

  return {
    [CALL_API]: {
      types: [CREATE, CREATE_SUCCESS, CREATE_FAILURE],
      endpoint: (state) => getConfig(state).services.graphqlUri,
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          input: values,
        },
      }),
      headers: commonHeaders,
    },
  };
};
export function cleanUpString(word) {
  let outString = word.replace(/[^\w\s]|_/g, '').replace(/\s+/g, ' ');
  outString = outString.toLowerCase().split(' ');
  for (let i = 0; i < outString.length; i++) {
    outString[i] = outString[i].charAt(0).toUpperCase() + outString[i].slice(1);
  }
  return outString.join(' ');
}

export function getDefaultEngineName(engineDetail) {
  const capabilityName = cleanUpString(engineDetail.engineCapability.name);
  const engineMode = cleanUpString(engineDetail.manifest.engineMode);
  const org = cleanUpString(engineDetail.orgName);
  const defaultEngineName = `${capabilityName}-${org}-${engineMode}-V3`;
  return defaultEngineName;
}



// WEBPACK FOOTER //
// ./src/client/modules/engine/index.js